// src/pages/dashboard/admin/complaints/Complaints.jsx
import { useState, useEffect } from 'react';
import api from '../../../../lib/api';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import Button from '../../../../components/layout/ui/Button';
import { formatDate } from '../../../../lib/helpers';
import { MessageSquare, User, Image as ImageIcon, X, CheckCircle, Ban, Clock } from 'lucide-react';
import { useConfirm } from '../../../../hooks/useConfirm';
import { success, error } from '../../../../components/common/Toast';

export default function AdminComplaints() {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [status, setStatus] = useState('resolved');
  const [showLightbox, setShowLightbox] = useState(null);

  const { confirm } = useConfirm();

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      const res = await api.get('/admin/complaints');
      setComplaints(res.data.data.data || []);
    } catch {
      error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  const resolveComplaint = async () => {
    if (!resolutionNotes.trim()) return error('Please provide resolution notes');

    const action = status === 'resolved' ? 'Resolve' : 'Dismiss';
    const confirmed = await confirm({
      title: `${action} This Complaint?`,
      message: `This will mark the complaint as ${status}. This action cannot be undone.`,
      confirmText: `Yes, ${action} Complaint`,
      cancelText: "Cancel",
      danger: status === 'dismissed',
    });

    if (!confirmed) return;

    try {
      await api.post(`/admin/complaints/${selectedComplaint.id}/resolve`, {
        resolution_notes: resolutionNotes,
        status: status,
      });
      success(`Complaint ${status === 'resolved' ? 'resolved' : 'dismissed'} successfully!`);
      setSelectedComplaint(null);
      setResolutionNotes('');
      setStatus('resolved');
      fetchComplaints();
    } catch {
      error('Failed to update complaint');
    }
  };

  const getFiles = (filePath) => {
    if (!filePath) return [];
    try {
      return JSON.parse(filePath).map(p => 
        `${process.env.REACT_APP_IMAGE_URL}storage/${p.replace(/\\/g, '/')}`
      );
    } catch {
      return [];
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' };
      case 'resolved':
        return { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Resolved' };
      case 'dismissed':
        return { color: 'bg-red-100 text-red-800', icon: Ban, label: 'Dismissed' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: status };
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="max-w-7xl mx-auto py-12 px-6">
      <h1 className="text-5xl font-black text-center mb-16">General Complaints</h1>

      {complaints.length === 0 ? (
        <div className="text-center py-32 bg-gray-50 rounded-3xl">
          <MessageSquare size={120} className="mx-auto text-gray-300 mb-8" />
          <p className="text-3xl text-gray-600">No complaints filed</p>
        </div>
      ) : (
        <div className="space-y-12">
          {complaints.map((c) => {
            const files = getFiles(c.file_path);
            const badge = getStatusBadge(c.status);

            return (
              <div key={c.id} className="bg-white rounded-3xl shadow-2xl overflow-hidden border-l-12 border-orange-500">
                <div className="p-10 lg:p-16">
                  {/* Header */}
                  <div className="flex flex-col lg:flex-row justify-between items-start gap-8 mb-10">
                    <div className="flex items-center gap-8">
                      <div className="w-20 h-20 bg-orange-100 rounded-3xl flex items-center justify-center">
                        <User size={48} className="text-orange-600" />
                      </div>
                      <div>
                        <h3 className="text-3xl font-bold">{c.profile.first_name + " " + c.profile.last_name}</h3>
                        <p className="text-xl text-gray-600">Complaint #{c.id}</p>
                        <p className="text-gray-500 mt-2">{formatDate(c.created_at)}</p>
                      </div>
                    </div>

                    <div className="text-right">
                      <span className={`inline-flex items-center gap-4 px-8 py-4 rounded-full font-bold text-2xl shadow-lg ${badge.color}`}>
                        <badge.icon size={36} />
                        {badge.label}
                      </span>
                    </div>
                  </div>

                  {/* Complaint Text */}
                  <div className="bg-gray-50 p-10 rounded-3xl mb-10">
                    <p className="text-xl leading-relaxed text-gray-800 whitespace-pre-line">
                      {c.complaint_details}
                    </p>
                  </div>

                  {/* Evidence */}
                  {files.length > 0 && (
                    <div className="mb-10">
                      <h4 className="text-2xl font-bold mb-6">Evidence ({files.length})</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {files.map((url, i) => (
                          <button
                            key={i}
                            onClick={() => setShowLightbox(url)}
                            className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition"
                          >
                            <img src={url} alt="Evidence" className="w-full h-48 object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                              <ImageIcon size={40} className="text-white" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Button */}
                  {c.status === 'pending' && (
                    <div className="text-center">
                      <Button onClick={() => setSelectedComplaint(c)} className="px-16 py-6 text-2xl">
                        Take Action
                      </Button>
                    </div>
                  )}

                  {/* Resolution Notes */}
                  {c.resolution_notes && (
                    <div className={`p-10 rounded-3xl border-4 ${c.status === 'resolved' ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                      <h4 className={`text-2xl font-bold ${c.status === 'resolved' ? 'text-green-800' : 'text-red-800'} mb-4 flex items-center gap-4`}>
                        {c.status === 'resolved' ? <CheckCircle size={36} /> : <Ban size={36} />}
                        {c.status === 'resolved' ? 'Resolved' : 'Dismissed'}
                      </h4>
                      <p className="text-xl leading-relaxed whitespace-pre-line">
                        {c.resolution_notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {showLightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setShowLightbox(null)}>
          <button className="absolute top-8 right-8 text-white hover:bg-white/10 rounded-full p-4 transition">
            <X size={48} />
          </button>
          <img src={showLightbox} alt="Evidence" className="max-w-full max-h-full rounded-2xl shadow-2xl" />
        </div>
      )}

      {/* Resolve Modal */}
      {selectedComplaint && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelectedComplaint(null)}>
          <div className="bg-white rounded-3xl max-w-4xl w-full p-10" onClick={e => e.stopPropagation()}>
            <h2 className="text-4xl font-black mb-8">Resolve Complaint #{selectedComplaint.id}</h2>

            <div className="space-y-8">
              <div>
                <label className="text-2xl font-bold">Decision</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full mt-3 px-6 py-5 rounded-2xl border-2 text-xl"
                >
                  <option value="resolved">Mark as Resolved</option>
                  <option value="dismissed">Dismiss Complaint</option>
                </select>
              </div>

              <div>
                <label className="text-2xl font-bold block mb-3">Resolution Notes</label>
                <textarea
                  rows={8}
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Explain your decision in detail..."
                  className="w-full px-6 py-5 rounded-2xl border-2 text-lg focus:ring-4 focus:ring-primary/20"
                />
              </div>

              <div className="flex justify-end gap-6">
                <Button variant="outline" onClick={() => setSelectedComplaint(null)}>
                  Cancel
                </Button>
                <Button 
                  onClick={resolveComplaint}
                  className={`text-xl px-12 py-5 ${status === 'dismissed' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {status === 'resolved' ? 'Mark as Resolved' : 'Dismiss Complaint'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}